import { useEffect, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

const CustomCursor = () => {
  const [isHovering, setIsHovering] = useState(false);

  // Use raw Framer Motion values to avoid triggering React component renders on every mousemove.
  // This yields 120fps+ native performance.
  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);

  // Snappy high-stiffness spring for the inner dot
  const dotX = useSpring(useTransform(cursorX, (x) => x - 8), { stiffness: 1200, damping: 50 });
  const dotY = useSpring(useTransform(cursorY, (y) => y - 8), { stiffness: 1200, damping: 50 });

  // Sleek inertia spring for the outer trailing ring
  const ringX = useSpring(useTransform(cursorX, (x) => x - 16), { stiffness: 600, damping: 35 });
  const ringY = useSpring(useTransform(cursorY, (y) => y - 16), { stiffness: 600, damping: 35 });

  useEffect(() => {
    const updateMousePosition = (e) => {
      cursorX.set(e.clientX);
      cursorY.set(e.clientY);
    };

    const handleMouseOver = (e) => {
      if (
        e.target.tagName === 'BUTTON' ||
        e.target.tagName === 'A' ||
        e.target.closest('button') ||
        e.target.closest('a')
      ) {
        setIsHovering(true);
      } else {
        setIsHovering(false);
      }
    };

    window.addEventListener('mousemove', updateMousePosition, { passive: true });
    document.addEventListener('mouseover', handleMouseOver, { passive: true });

    return () => {
      window.removeEventListener('mousemove', updateMousePosition);
      document.removeEventListener('mouseover', handleMouseOver);
    };
  }, [cursorX, cursorY]);

  // Hide on mobile
  if (typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches) {
    return null;
  }

  return (
    <>
      {/* Inner Dot */}
      <motion.div
        className="fixed w-4 h-4 bg-cyan-500 rounded-full pointer-events-none mix-blend-difference z-[9999]"
        style={{
          x: dotX,
          y: dotY,
        }}
        animate={{
          scale: isHovering ? 2 : 1,
        }}
        transition={{ duration: 0.15 }}
      />
      {/* Outer Ring */}
      <motion.div
        className="fixed w-8 h-8 border border-cyan-500/50 rounded-full pointer-events-none mix-blend-difference z-[9999]"
        style={{
          x: ringX,
          y: ringY,
        }}
        animate={{
          scale: isHovering ? 1.6 : 1,
        }}
        transition={{ duration: 0.2 }}
      />
    </>
  );
};

export default CustomCursor;